import { createCommand, COMMAND_PRIORITY_HIGH, $getSelection, $isRangeSelection } from 'lexical'
import { $insertText } from '@/lib/lexical/utils'
import { MONERO_WALL_MARKER } from '@/lib/monero-wall'
import { mergeRegister } from '@lexical/utils'

export const MD_FORMAT_COMMAND = createCommand('MD_FORMAT_COMMAND')
export const MD_INSERT_BLOCK_COMMAND = createCommand('MD_INSERT_BLOCK_COMMAND')

function $prefixEachLine (getPrefixFn) {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) return false

  const selectedText = selection.getTextContent()
  const lines = selectedText.split('\n')
  const markdown = lines.map((line, index) => getPrefixFn(line, index)).join('\n')
  $insertText(markdown)

  return true
}

function $wrapMarkdownSelection (prefix, suffix = prefix, cursorOffset = suffix.length) {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) return false

  const selectedText = selection.getTextContent()
  selection.insertText(`${prefix}${selectedText}${suffix}`)

  const newSelection = $getSelection()
  if ($isRangeSelection(newSelection)) {
    const anchor = newSelection.anchor
    const newOffset = anchor.offset - cursorOffset
    anchor.set(anchor.key, newOffset, anchor.type)
    newSelection.focus.set(anchor.key, newOffset, anchor.type)
  }
  return true
}

const FORMAT_HANDLERS = {
  bold: () => $wrapMarkdownSelection('**'),
  italic: () => $wrapMarkdownSelection('*'),
  link: () => $wrapMarkdownSelection('[', ']()', 1),
  code: () => $wrapMarkdownSelection('`'),
  superscript: () => $wrapMarkdownSelection('<sup>', '</sup>'),
  subscript: () => $wrapMarkdownSelection('<sub>', '</sub>'),
  underline: () => $wrapMarkdownSelection('<ins>', '</ins>'),
  strikethrough: () => $wrapMarkdownSelection('~~'),
  inlineMath: () => $wrapMarkdownSelection('$$'),
  math: () => $wrapMarkdownSelection('$$\n', '\n$$', 3),
  moneroWall: () => $insertText(`\n${MONERO_WALL_MARKER}\n`)
}

const BLOCK_HANDLERS = {
  h1: () => $prefixEachLine((line) => `# ${line}`),
  h2: () => $prefixEachLine((line) => `## ${line}`),
  h3: () => $prefixEachLine((line) => `### ${line}`),
  number: () => $prefixEachLine((line, index) => `${index + 1}. ${line}`),
  check: () => $prefixEachLine((line) => `- [ ] ${line}`),
  bullet: () => $prefixEachLine((line) => `- ${line}`),
  quote: () => $prefixEachLine(line => `> ${line}`),
  code: () => handleCodeblockCommand()
}

function handleCodeblockCommand ({ language = 'text' } = {}) {
  const selection = $getSelection()
  if (!$isRangeSelection(selection)) return false

  const selectedText = selection.getTextContent()
  $insertText(`\`\`\`${language}\n${selectedText}\n\`\`\``, false, { line: 0, anchorOffset: 1 })
  return true
}

function handleMDFormatCommand (type) {
  return FORMAT_HANDLERS[type]?.() ?? false
}

function handleMDInsertBlockCommand (type) {
  return BLOCK_HANDLERS[type]?.() ?? false
}

export function registerMDFormatCommand (editor) {
  return mergeRegister(
    editor.registerCommand(MD_FORMAT_COMMAND, handleMDFormatCommand, COMMAND_PRIORITY_HIGH),
    editor.registerCommand(MD_INSERT_BLOCK_COMMAND, handleMDInsertBlockCommand, COMMAND_PRIORITY_HIGH)
  )
}
